import React, { useState, useEffect } from 'react';
import { Loader2, Mail, Phone, Trash2, Users, Clock, CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const AdminWaitlist = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total_entries: 0, total_pending: 0, products: [] });
  const [expanded, setExpanded] = useState({});

  const fetchWaitlist = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/waitlist`, { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (err) {
      console.error('Failed to fetch waitlist:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWaitlist();
  }, []);

  const handleDelete = async (entryId) => {
    if (!window.confirm('Supprimer cette inscription ?')) return;
    try {
      const res = await fetch(`${API}/admin/waitlist/${entryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) fetchWaitlist();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const toggle = (pid) => setExpanded(prev => ({ ...prev, [pid]: !prev[pid] }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-fgs-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-waitlist-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Liste d'attente</h1>
          <p className="text-fgs-text-muted text-sm">Clients prévenus dès qu'un produit "Bientôt disponible" est mis en ligne</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchWaitlist}
          className="border-fgs-border text-white"
          data-testid="refresh-waitlist-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-fgs-surface border border-fgs-border rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{data.total_entries}</p>
              <p className="text-xs text-fgs-text-muted">Inscriptions totales</p>
            </div>
          </div>
        </div>
        <div className="bg-fgs-surface border border-fgs-border rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{data.total_pending}</p>
              <p className="text-xs text-fgs-text-muted">En attente de notification</p>
            </div>
          </div>
        </div>
        <div className="bg-fgs-surface border border-fgs-border rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{data.total_entries - data.total_pending}</p>
              <p className="text-xs text-fgs-text-muted">Déjà notifiés</p>
            </div>
          </div>
        </div>
      </div>

      {/* Empty state */}
      {data.products.length === 0 && (
        <div className="bg-fgs-surface border border-fgs-border rounded-xl p-12 text-center">
          <Mail className="w-12 h-12 text-fgs-text-muted mx-auto mb-4" />
          <h3 className="text-white font-semibold mb-2">Aucune inscription pour l'instant</h3>
          <p className="text-fgs-text-muted text-sm">
            Lorsqu'un client s'inscrira pour être prévenu d'un produit "Bientôt disponible", il apparaîtra ici.
          </p>
        </div>
      )}

      {/* Products with waitlist */}
      <div className="space-y-4">
        {data.products.map(product => (
          <div
            key={product.product_id}
            className="bg-fgs-surface border border-fgs-border rounded-xl overflow-hidden"
          >
            <button
              onClick={() => toggle(product.product_id)}
              className="w-full p-5 flex items-center justify-between hover:bg-fgs-surface-highlight transition-colors"
            >
              <div className="text-left">
                <h3 className="text-white font-semibold">{product.product_name}</h3>
                <p className="text-xs text-fgs-text-muted mt-1">
                  {product.count} inscription{product.count > 1 ? 's' : ''} — {product.pending_count} en attente
                </p>
              </div>
              <div className="flex items-center gap-3">
                {product.pending_count > 0 && (
                  <span className="px-2.5 py-1 bg-amber-500/10 text-amber-400 text-xs font-bold rounded-full">
                    🔜 {product.pending_count} en attente
                  </span>
                )}
                <span className="text-white/40 text-sm">{expanded[product.product_id] ? '▲' : '▼'}</span>
              </div>
            </button>

            {expanded[product.product_id] && (
              <div className="border-t border-fgs-border">
                <table className="w-full">
                  <thead className="bg-fgs-bg">
                    <tr>
                      <th className="text-left text-xs uppercase text-fgs-text-muted font-medium py-3 px-5">Nom</th>
                      <th className="text-left text-xs uppercase text-fgs-text-muted font-medium py-3 px-5">Email</th>
                      <th className="text-left text-xs uppercase text-fgs-text-muted font-medium py-3 px-5">Téléphone</th>
                      <th className="text-left text-xs uppercase text-fgs-text-muted font-medium py-3 px-5">Inscription</th>
                      <th className="text-left text-xs uppercase text-fgs-text-muted font-medium py-3 px-5">Statut</th>
                      <th className="text-right text-xs uppercase text-fgs-text-muted font-medium py-3 px-5">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.entries.map(entry => (
                      <tr key={entry.entry_id} className="border-t border-fgs-border/50">
                        <td className="py-3 px-5 text-white text-sm">{entry.name || '—'}</td>
                        <td className="py-3 px-5 text-fgs-text-muted text-sm font-mono">
                          <a href={`mailto:${entry.email}`} className="hover:text-emerald-400">{entry.email}</a>
                        </td>
                        <td className="py-3 px-5 text-fgs-text-muted text-sm">
                          {entry.phone ? (
                            <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{entry.phone}</span>
                          ) : '—'}
                        </td>
                        <td className="py-3 px-5 text-fgs-text-muted text-xs">
                          {new Date(entry.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="py-3 px-5">
                          {entry.notified_at ? (
                            <span className="inline-flex items-center gap-1 text-emerald-400 text-xs">
                              <CheckCircle2 className="w-3 h-3" /> Notifié
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-400 text-xs">
                              <Clock className="w-3 h-3" /> En attente
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-5 text-right">
                          <button
                            onClick={() => handleDelete(entry.entry_id)}
                            data-testid={`delete-waitlist-${entry.entry_id}`}
                            className="text-red-400 hover:text-red-300 transition-colors p-1"
                            title="Supprimer cette inscription"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl">
        <p className="text-xs text-blue-300">
          💡 <strong>Astuce</strong> : dès que vous décochez "🔜 Bientôt disponible" pour un produit dans le panneau Produits, un email est automatiquement envoyé à tous les inscrits ci-dessus.
        </p>
      </div>
    </div>
  );
};

export default AdminWaitlist;
